import {HttpClient} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {environment} from '@environments/environment';
import {firstValueFrom} from 'rxjs';

interface RedeStatus {
  ip: string;
  redeInterna: boolean;
  precisaProva: boolean;
  urlCodigo: string;
}

/**
 * Visitante (sem login) só de dentro da rede da ARTESP, igual ao SIGEP.
 * O PROD recebe todo mundo com o IP do equipamento de entrada, então quando a
 * API pede "prova", o navegador busca um código no HOM (que só abre de dentro
 * da rede) e a API do PROD confere com o HOM e devolve um passe de 8 h, que
 * vai no cabeçalho X-Sisnot-Rede (authInterceptor).
 */
@Injectable({
  providedIn: 'root',
})
export class RedeService {
  static readonly CABECALHO = 'X-Sisnot-Rede';
  private readonly STORAGE_KEY_PASSE = 'redePasse';
  private readonly TEMPO_PROVA_MS = 3500;
  private http = inject(HttpClient);
  private redeUri = environment.api.uri.concat('rede');
  private verificacao: Promise<boolean> | null = null;

  getPasse(): string | null {
    try {
      return localStorage.getItem(this.STORAGE_KEY_PASSE);
    } catch {
      return null;
    }
  }

  /** Esquece o resultado (passe vencido / API respondeu FORA_DA_REDE). */
  limpar() {
    this.verificacao = null;
    try {
      localStorage.removeItem(this.STORAGE_KEY_PASSE);
    } catch {
      // sem storage: só não guarda o passe
    }
  }

  /** Está na rede da ARTESP? Faz a prova pelo HOM se precisar. Guarda o resultado positivo. */
  verificar(): Promise<boolean> {
    if (!this.verificacao) {
      this.verificacao = this.verificarAgora().then(ok => {
        if (!ok) this.verificacao = null;
        return ok;
      });
    }
    return this.verificacao;
  }

  private async verificarAgora(): Promise<boolean> {
    try {
      const status = await firstValueFrom(this.http.get<RedeStatus>(`${this.redeUri}/status`));
      if (status.redeInterna) return true;
      if (!status.precisaProva || !status.urlCodigo) return false;

      const codigo = await this.buscarCodigoNoHom(status.urlCodigo);
      if (!codigo) return false;

      const resposta = await firstValueFrom(
        this.http.post<{ ok: boolean; passe?: string }>(`${this.redeUri}/confirmar`, {codigo})
      );
      if (!resposta.ok || !resposta.passe) return false;
      try {
        localStorage.setItem(this.STORAGE_KEY_PASSE, resposta.passe);
      } catch {
        return false;
      }
      return true;
    } catch {
      return false;
    }
  }

  /** fetch direto (sem o interceptor): sem token e sem cookie pro HOM. */
  private async buscarCodigoNoHom(url: string): Promise<string | null> {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), this.TEMPO_PROVA_MS);
    try {
      const r = await fetch(url, {mode: 'cors', credentials: 'omit', cache: 'no-store', signal: ctl.signal});
      if (!r.ok) return null;
      const d = await r.json();
      return d?.codigo ?? null;
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }
}
